import { ElementRef, inject, Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import { LookupService } from '@app/core/service';
import { FormDialogComponent } from '@app/shared/components';
import { FormDialogConfig, InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { InboundFaxAction } from '@mynexus/mynexus-ui-lib';
import { DialogService } from 'primeng/dynamicdialog';
import { of } from 'rxjs';

@Injectable()
export class CreateNewAuthorizationService {
  private readonly dialogService = inject(DialogService);
  private readonly lookupService = inject(LookupService);
  private readonly ref = inject(ElementRef);

  public async handleClick(target: InboundFaxesTableModel, inbox: InboxDto): Promise<void> {
    const formDialogConfigs = await this.getFormDialogConfigs(target, inbox);

    this.dialogService
      .open(
        FormDialogComponent,
        DialogConfig('Create New Authorization', {
          submitLabel: 'Next',
          formDialogConfigs: of(formDialogConfigs),
        })
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          const data: InboundFaxAction = {
            authorizationNumber: res['authorizationNumber'],
            description: res['description'],
            documentType: res['documentType'],
            faxReceivedDateTime: target.faxReceivedDateTime,
            fileName: target.documentStorageLocation,
            filePath: `${inbox.basePath}/${target.documentStorageLocation}`,
            fileSizeBytes: target.fileSizeBytes,
            inboundFaxId: target.inboundFaxId,
            memberId: res['memberId'],
            productName: inbox.tenantLobProduct.productShortName,
            memberSubscriberNumber: target.memberSubscriberNumber,
            tenantId: inbox.tenantLobProduct.tenantId,
            tenantLobProductId: inbox.tenantLobProduct.tenantLobProductId,
          };

          this.navigateToAuthorization(data);
        }
      });
  }

  private async getFormDialogConfigs(target: InboundFaxesTableModel, inbox: InboxDto): Promise<FormDialogConfig[]> {
    const documentTypes = (await this.lookupService.getDocumentTypes(inbox)).auth.map((x) => {
      return { label: x.description, value: x.description };
    });

    return [
      {
        label: 'Member ID:',
        type: 'text',
        text: target.memberSubscriberNumber,
        formControlName: 'memberId',
      },
      {
        label: 'Name:',
        type: 'text',
        text: target.memberFullName,
        formControlName: 'memberName',
      },
      {
        label: 'Date of Birth:',
        type: 'text',
        text: target.memberDOB,
        formControlName: 'dateOfBirth',
      },
      {
        label: 'Document Type:',
        type: 'multiselect',
        formControlName: 'documentType',
        options: documentTypes,
        validators: [Validators.required],
      },
      {
        label: 'Description:',
        type: 'input',
        formControlName: 'description',
        charCounter: true,
        maxLength: 40,
      },
    ];
  }

  private navigateToAuthorization(data: InboundFaxAction): void {
    this.ref.nativeElement.dispatchEvent(
      new CustomEvent('handleAuthorizationCreation', {
        bubbles: true,
        detail: { data },
      })
    );
  }
}
